import { useRef, useState } from 'react';
import { stringContains } from 'utils/StringUtils';
import { groupBy } from 'utils/ArrayUtils';
import { StepDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/StepDisplayData';
import './StepsTree.less';
import { SearchField } from 'components/inputs/SearchField';
import { Tree } from 'components/Tree/CommonTree/Tree';
import { useCreateTreeStore } from 'components/Tree/CommonTree/useTreeStore';
import { generateGuid } from 'utils/Guid';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { useDidUpdateEffect } from 'hooks/useDidUpdateEffect';
import { useUpdateReason } from 'hooks/useUpdateReason';

export enum StepsTreeType {
  /**
   * Explorer tree shows all the steps which are used in the flow
   */
  explorer,
  /**
   * "ByType" tree shows all step types which are used in the flow
   */
  byType,
}
export interface IStepsTreeProps {
  steps: StepDisplayData[];
  onNodeSelected: (stepId?: string | string[]) => void;
  type: StepsTreeType;
}

interface IStepNode {
  id: string;
  name: string;
  ids?: string[];
}

export function StepsTree({ steps, onNodeSelected, type }: IStepsTreeProps) {
  const id = useRef(`steps-tree-${generateGuid()}`);
  const [searchString, setSearch] = useState<string>('');

  if (IS_TEST) {
    // Test "shows only matched nodes" fails from time to time so added this temporary logging
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useUpdateReason('StepsTree', {
      steps,
      type,
      searchString,
    });
  }

  const useStore = useCreateTreeStore({
    treeId: id.current,
    nodes: getNodes(steps, type, searchString),
    onNodeSelected: node => {
      onNodeSelected(type === StepsTreeType.explorer ? node.nodeId : node.data?.ids);
    },
  });
  const { nodes, actions } = useStore();
  const handleClear = () => setSearch('');

  useDidUpdateEffect(() => {
    if (IS_TEST) {
      console.log(`Updating nodes. Search string: ${searchString}`);
    }
    actions.replaceNodes(getNodes(steps, type, searchString));
  }, [actions, searchString, steps, type]);

  const handleNodeSelected = (node: ITreeNode<IStepNode>) => {
    actions.selectNode(node.nodeId);
  };

  return (
    <div className="dp-steps-tree">
      <div className="dp-steps-tree__search-box">
        <SearchField onSearch={setSearch} onClear={handleClear} value={searchString} />
      </div>
      <Tree
        classes={{ root: 'dp-steps-tree__tree' }}
        nodes={nodes}
        searchString={searchString}
        onNodeSelect={handleNodeSelected}
      />
    </div>
  );
}

function getNodes(steps: StepDisplayData[], type: StepsTreeType, searchString: string) {
  let nodes: ITreeNode<IStepNode>[] = buildStepsByType(steps, type).map(step => ({
    nodeId: step.id,
    name: step.name,
    description: type === StepsTreeType.byType ? `(${step.ids?.length})` : undefined,
    data: {
      id: step.id,
      name: step.name,
      ids: step.ids,
    },
    isActive: false,
    isExpanded: false,
    isSelectable: true,
  }));

  if (searchString?.length) {
    nodes = nodes.filter(node => stringContains(node.name, searchString, true));
  }

  return nodes;
}

function buildStepsByType(steps: StepDisplayData[], type: StepsTreeType): IStepNode[] {
  if (type === StepsTreeType.byType) {
    let groupSteps = groupBy<StepDisplayData, string>(steps, step => step.StepType);
    return Object.keys(groupSteps)
      .sort((prev, next) => prev.localeCompare(next))
      .map(stepType => {
        let groupStep = groupSteps[stepType];
        return {
          id: groupStep[0].StepID,
          name: stepType,
          ids: groupStep.map(({ StepID }) => StepID),
        };
      });
  }

  if (type === StepsTreeType.explorer) {
    return steps.map(({ StepName, StepType, StepID }) => ({
      id: StepID,
      name: StepName || StepType,
    }));
  }

  return [];
}
